import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Loader2, FileText, PoundSterling, Sparkles, Copy, Send, RefreshCw, CheckCircle2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_META = {
  awaiting_payment: { label: 'Awaiting payment', c: 'bg-gray-100 text-gray-600' },
  paid: { label: 'Paid', c: 'bg-amber-100 text-amber-700' },
  generating: { label: 'Generating', c: 'bg-amber-100 text-amber-700' },
  draft: { label: 'Draft (review)', c: 'bg-blue-100 text-blue-700' },
  approved: { label: 'Approved', c: 'bg-emerald-100 text-emerald-700' },
  sent: { label: 'Sent', c: 'bg-emerald-100 text-emerald-700' },
  failed: { label: 'Failed', c: 'bg-red-100 text-red-700' },
};
const fmt = (s) => { try { return new Date(s).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }); } catch { return s; } };

const ReportDetail = ({ id, onClose, onChanged }) => {
  const [d, setD] = useState(null);
  const [rep, setRep] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const { data } = await axios.get(`${API}/procurement-reports/${id}`);
    setD(data); setRep(data.final_report || data.ai_report || null);
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const saveEdits = async () => {
    setBusy(true);
    try { await axios.patch(`${API}/procurement-reports/${id}`, { final_report: rep }); toast.success('Draft saved'); }
    catch { toast.error('Save failed'); } finally { setBusy(false); }
  };
  const setStatus = async (status) => {
    setBusy(true);
    try {
      if (rep) await axios.patch(`${API}/procurement-reports/${id}`, { final_report: rep });
      await axios.post(`${API}/procurement-reports/${id}/status`, { status });
      toast.success(status === 'sent' ? 'Report sent to buyer' : `Marked ${status}`);
      onChanged(); load();
    } catch { toast.error('Update failed'); } finally { setBusy(false); }
  };
  const regenerate = async () => {
    setBusy(true);
    try { await axios.post(`${API}/procurement-reports/${id}/regenerate`); toast.success('Regenerating - refresh in a few seconds'); onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } finally { setBusy(false); }
  };
  const copyLink = () => { if (d?.report_link) { navigator.clipboard.writeText(d.report_link); toast.success('Link copied'); } };
  const setField = (k, v) => setRep((p) => ({ ...(p || {}), [k]: v }));

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="report-detail-dialog">
        <DialogHeader><DialogTitle>{d ? `${d.label} report - ${d.organisation || d.email}` : 'Report'}</DialogTitle></DialogHeader>
        {!d ? <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div> : (
          <div className="space-y-5">
            <div className="text-sm text-gray-600">
              <Badge className={STATUS_META[d.status]?.c}>{STATUS_META[d.status]?.label || d.status}</Badge>
              <span className="ml-2">{d.email} · {d.payment_status === 'paid' ? `Paid £${d.amount}` : 'Unpaid'}</span>
            </div>

            <div className="border border-[#E5E7EB] rounded-lg p-4">
              <p className="text-sm font-semibold text-gray-900 mb-2">Buyer answers</p>
              <div className="space-y-1 text-sm text-gray-600">
                {(d.questions || []).map((q) => (
                  <p key={q.key}><span className="text-gray-800 font-medium">{q.label}:</span> {String(d.answers?.[q.key] ?? '-')}</p>
                ))}
              </div>
            </div>

            {d.status === 'paid' || d.status === 'generating' ? (
              <div className="border border-amber-200 bg-amber-50 rounded-lg p-4 text-sm text-amber-800 flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" /> The draft is generating. Close and reopen in a few seconds.
                <Button size="sm" variant="outline" onClick={regenerate} disabled={busy} className="ml-auto h-7">Retry</Button>
              </div>
            ) : rep ? (
              <div className="space-y-4" data-testid="report-editor">
                <div>
                  <label className="text-xs font-semibold text-gray-500">Summary</label>
                  <textarea rows={3} value={rep.summary || ''} onChange={(e) => setField('summary', e.target.value)} className="w-full mt-1 text-sm px-3 py-2 border border-[#E5E7EB] rounded-md" data-testid="report-edit-summary" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-500">Recommendations</label>
                  {(rep.recommendations || []).map((rec, i) => (
                    <div key={i} className="grid grid-cols-1 gap-1.5 mt-1 border border-[#E5E7EB] rounded-md p-2">
                      <input value={rec.supplier_name || ''} onChange={(e) => { const a = [...rep.recommendations]; a[i] = { ...a[i], supplier_name: e.target.value }; setField('recommendations', a); }} placeholder="Supplier name" className="text-sm px-2 py-1.5 border border-[#E5E7EB] rounded" />
                      <textarea rows={2} value={rec.why || ''} onChange={(e) => { const a = [...rep.recommendations]; a[i] = { ...a[i], why: e.target.value }; setField('recommendations', a); }} placeholder="Why" className="text-sm px-2 py-1.5 border border-[#E5E7EB] rounded" />
                    </div>
                  ))}
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-500">Guidance (one per line)</label>
                  <textarea rows={4} value={(rep.guidance || []).join('\n')} onChange={(e) => setField('guidance', e.target.value.split('\n').filter(Boolean))} className="w-full mt-1 text-sm px-3 py-2 border border-[#E5E7EB] rounded-md" data-testid="report-edit-guidance" />
                </div>
                <div className="grid grid-cols-1 gap-3">
                  <div><label className="text-xs font-semibold text-gray-500">Estimated savings</label><input value={rep.estimated_savings || ''} onChange={(e) => setField('estimated_savings', e.target.value)} className="w-full mt-1 text-sm px-3 py-2 border border-[#E5E7EB] rounded-md" /></div>
                  <div><label className="text-xs font-semibold text-gray-500">Next steps</label><textarea rows={2} value={rep.next_steps || ''} onChange={(e) => setField('next_steps', e.target.value)} className="w-full mt-1 text-sm px-3 py-2 border border-[#E5E7EB] rounded-md" /></div>
                </div>
              </div>
            ) : <p className="text-sm text-gray-400">No draft yet.</p>}

            {d.report_link && (
              <div className="flex items-center gap-2">
                <input readOnly value={d.report_link} className="flex-1 text-xs px-3 py-2 border border-[#E5E7EB] rounded-md bg-gray-50 text-gray-600" data-testid="report-link-input" />
                <Button size="sm" variant="outline" onClick={copyLink} className="h-8"><Copy className="w-3.5 h-3.5" /></Button>
              </div>
            )}

            <div className="flex flex-wrap gap-2 border-t border-gray-100 pt-4">
              {rep && <Button size="sm" variant="outline" onClick={saveEdits} disabled={busy} data-testid="report-save">Save draft</Button>}
              {['draft', 'approved'].includes(d.status) && <Button size="sm" onClick={() => setStatus('approved')} disabled={busy} className="bg-[#00A79D] hover:bg-[#00857D]" data-testid="report-approve"><CheckCircle2 className="w-4 h-4 mr-1" /> Approve</Button>}
              {['draft', 'approved'].includes(d.status) && <Button size="sm" onClick={() => setStatus('sent')} disabled={busy} className="bg-[#00A79D] hover:bg-[#00857D]" data-testid="report-send"><Send className="w-4 h-4 mr-1" /> Approve & send</Button>}
              {rep && <Button size="sm" variant="outline" onClick={regenerate} disabled={busy} data-testid="report-regenerate"><RefreshCw className="w-4 h-4 mr-1" /> Regenerate</Button>}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const ProcurementReports = () => {
  const [data, setData] = useState({ items: [], counts: {}, paid: 0, revenue: 0 });
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await axios.get(`${API}/procurement-reports`); setData(r.data); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="flex items-center justify-center py-32"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>;

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6" data-testid="procurement-reports-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Procurement reports</h1>
        <p className="text-gray-500 text-sm mt-1">Paid personalised procurement reports. Review and edit each draft, then approve and send to the buyer.</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card data-testid="reports-stat-paid"><CardContent className="p-5 flex items-center gap-4"><div className="w-11 h-11 rounded-lg bg-[#00A79D]/10 flex items-center justify-center"><FileText className="w-5 h-5 text-[#00A79D]" /></div><div><p className="text-2xl font-bold text-gray-900">{data.paid}</p><p className="text-sm text-gray-500">Paid reports</p></div></CardContent></Card>
        <Card data-testid="reports-stat-revenue"><CardContent className="p-5 flex items-center gap-4"><div className="w-11 h-11 rounded-lg bg-emerald-100 flex items-center justify-center"><PoundSterling className="w-5 h-5 text-emerald-600" /></div><div><p className="text-2xl font-bold text-gray-900">£{Number(data.revenue).toLocaleString()}</p><p className="text-sm text-gray-500">Revenue</p></div></CardContent></Card>
        <Card><CardContent className="p-5"><p className="text-2xl font-bold text-blue-700">{data.counts.draft || 0}</p><p className="text-sm text-gray-500">Awaiting review</p></CardContent></Card>
        <Card><CardContent className="p-5"><p className="text-2xl font-bold text-emerald-700">{data.counts.sent || 0}</p><p className="text-sm text-gray-500">Sent</p></CardContent></Card>
      </div>

      <div className="space-y-2">
        {data.items.length === 0 && <p className="text-sm text-gray-400 py-10 text-center border border-dashed border-gray-200 rounded-lg">No procurement reports yet.</p>}
        {data.items.map((r) => (
          <Card key={r.id} className="cursor-pointer hover:border-[#00A79D]/40 transition-colors" onClick={() => setSelected(r.id)} data-testid={`report-row-${r.id}`}><CardContent className="p-4 flex items-center gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <p className="font-semibold text-gray-900">{r.organisation || r.email}</p>
                <Badge variant="secondary" className="bg-[#00A79D]/10 text-[#00857D] text-[10px] capitalize">{r.area.replace('_', ' ')}</Badge>
                <Sparkles className="w-3.5 h-3.5 text-[#00A79D]" />
              </div>
              <p className="text-xs text-gray-400 mt-0.5">{r.email} · {fmt(r.created_at)}</p>
            </div>
            <Badge className={STATUS_META[r.status]?.c}>{STATUS_META[r.status]?.label || r.status}</Badge>
          </CardContent></Card>
        ))}
      </div>

      {selected && <ReportDetail id={selected} onClose={() => setSelected(null)} onChanged={load} />}
    </div>
  );
};

export default ProcurementReports;
